import React from "react";
import { Helmet } from "react-helmet-async";
import { Card, CardContent } from "@/components/ui/card";
import { Flame, Snowflake, Compass, MapPin, Users, Calendar, Layers, Rocket } from "lucide-react";
import { Separator } from "@/components/ui/separator";

const ProjectIntro: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>About SaunaDip | Ireland's Sauna & Cold Plunge Directory</title>
        <meta 
          name="description" 
          content="Learn about SaunaDip - a community-powered directory for sauna and cold plunge experiences across Ireland and beyond."
        />
      </Helmet>

      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-sauna-navy flex items-center justify-center gap-2">
            <Flame className="h-8 w-8 text-sauna-orange" />
            About SaunaDip
            <Snowflake className="h-8 w-8 text-sauna-blue" />
          </h1>
          <div className="h-1 w-32 divider-gradient rounded-full mx-auto mt-4"></div>
        </div>

        <Card className="mb-10 border-none shadow-lg">
          <CardContent className="p-8">
            <p className="text-lg text-sauna-navy/90">
              SaunaDip is a community-powered directory and discovery platform dedicated to helping people 
              find the best sauna and cold plunge experiences across Ireland — and soon, beyond.
            </p>
            <p className="text-lg mt-4 text-sauna-navy/90">
              Whether you're chasing the thrill of an icy dip by the sea or the warmth of a handcrafted 
              mobile sauna in the woods, we make it easy to explore, compare, and connect with wellness 
              experiences near you.
            </p>
            <p className="text-lg mt-6 text-sauna-navy/90">
              Curious about our story and what motivates us? <a href="/why-kit" className="text-sauna-orange underline hover:text-sauna-blue">Read why we're building the SaunaDip Kit</a>.
            </p>
          </CardContent>
        </Card>

        <section className="mb-12">
          <div className="flex items-center mb-6">
            <Compass className="h-7 w-7 text-sauna-orange mr-3" />
            <h2 className="text-2xl font-bold text-sauna-navy">Our Mission</h2>
          </div>
          <Card className="border-none shadow-md">
            <CardContent className="p-6">
              <p className="text-sauna-navy/80 mb-4">
                To inspire wellbeing and adventure by making thermal experiences more accessible, transparent, 
                and community-driven.
              </p>
              <p className="font-semibold mt-4 mb-2">We believe in:</p>
              <ul className="list-disc pl-6 space-y-2 text-sauna-navy/80">
                <li>The healing power of hot and cold therapy.</li>
                <li>Supporting local operators and small wellness businesses.</li>
                <li>Encouraging authentic, real-world reviews and connections.</li>
              </ul>
            </CardContent>
          </Card>
        </section>

        <section className="mb-12">
          <div className="flex items-center mb-6">
            <Layers className="h-7 w-7 text-sauna-orange mr-3" />
            <h2 className="text-2xl font-bold text-sauna-navy">How It Works</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <Card className="border-none shadow-md">
              <CardContent className="p-6">
                <h3 className="font-semibold text-lg mb-3 text-sauna-navy flex items-center">
                  <Calendar className="h-5 w-5 text-sauna-orange mr-2" />
                  Daily Discovery
                </h3>
                <p className="text-sauna-navy/80">
                  Our system finds new saunas and cold plunge services every night using trusted public data sources.
                </p>
                <p className="text-sauna-navy/80 mt-3">
                  New listings are reviewed and published daily, with a preview sent to the business owner for 
                  verification or removal.
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-md">
              <CardContent className="p-6">
                <h3 className="font-semibold text-lg mb-3 text-sauna-navy flex items-center">
                  <Users className="h-5 w-5 text-sauna-orange mr-2" />
                  Community Curation
                </h3>
                <p className="text-sauna-navy/80">
                  Users can browse by location, experience (e.g., forest retreat, lakefront), or type (sauna, plunge, steam).
                </p>
                <p className="text-sauna-navy/80 mt-3">
                  Operators can claim their listings, upload photos, update details, and receive insights about 
                  their listing's performance.
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-md">
              <CardContent className="p-6">
                <h3 className="font-semibold text-lg mb-3 text-sauna-navy flex items-center">
                  <MapPin className="h-5 w-5 text-sauna-orange mr-2" />
                  AI + Automation
                </h3>
                <p className="text-sauna-navy/80">
                  We use smart tools to summarise listings, map nearby waters, and recommend categorisations.
                </p>
                <p className="text-sauna-navy/80 mt-3">
                  You'll see tags like "Sea View," "Mobile Sauna," or "Guided Breathing" automatically generated 
                  based on the operator's website or social media.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="mb-12">
          <div className="flex items-center mb-6">
            <Rocket className="h-7 w-7 text-sauna-orange mr-3" />
            <h2 className="text-2xl font-bold text-sauna-navy">What's Coming Next</h2>
          </div>
          <Card className="border-none shadow-md">
            <CardContent className="p-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="flex items-start">
                  <MapPin className="h-5 w-5 text-sauna-orange mr-2 mt-0.5 flex-shrink-0" />
                  <p className="text-sauna-navy/80">Maps with filters for experiences, counties, and add-ons</p>
                </div>
                <div className="flex items-start">
                  <Users className="h-5 w-5 text-sauna-orange mr-2 mt-0.5 flex-shrink-0" />
                  <p className="text-sauna-navy/80">Operator dashboards to track clicks, interest, and bookings</p>
                </div>
                <div className="flex items-start">
                  <Calendar className="h-5 w-5 text-sauna-orange mr-2 mt-0.5 flex-shrink-0" />
                  <p className="text-sauna-navy/80">Live sensor integrations showing sauna temps in real time</p>
                </div>
                <div className="flex items-start">
                  <Compass className="h-5 w-5 text-sauna-orange mr-2 mt-0.5 flex-shrink-0" />
                  <p className="text-sauna-navy/80">Rewards & badges for those who share their experience or support the community</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        <section className="mb-12">
          <div className="flex items-center mb-6">
            <Users className="h-7 w-7 text-sauna-orange mr-3" />
            <h2 className="text-2xl font-bold text-sauna-navy">Want to Support the Project?</h2>
          </div>
          <Card className="border-none shadow-md">
            <CardContent className="p-6">
              <div className="text-center">
                <p className="text-sauna-navy/80 mb-2">
                  Follow us on Instagram: <a href="https://www.instagram.com/saunadip" className="text-sauna-orange hover:underline">@saunadip</a>
                </p>
                <p className="text-sauna-navy/80">
                  Or share your favourite sauna using <span className="font-semibold">#saunadip</span>
                </p>
              </div>
            </CardContent>
          </Card>
        </section>
      </div>
    </>
  );
};

export default ProjectIntro;
